// frontend/src/components/SignInModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import {
  niveauxDeSession, defiEnAttente, facteursPourLeDefi,
  presenterLeCodeSurChaqueFacteur, messageDefiEchoue, codeBienForme,
} from '../utils/mfaDefi';

/**
 * SignInModal
 * - mode="modal" (par défaut) : overlay + croix + fermeture
 * - mode="page"               : pas d'overlay, pas de croix
 *
 * Règles d'accès par rang :
 *   0 = super-admin  → /dashboard (vue admin)
 *   8 = auditeur     → /dashboard (vue admin)
 *   autre            → /dashboard (vue client)
 */
const SignInModal = ({ isOpen = true, onClose, mode = 'modal' }) => {
  const navigate = useNavigate();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  // Second facteur : 'motdepasse' puis, si le compte a la double authentification
  // (activée depuis le CRM), 'code'. Voir utils/mfaDefi.js.
  const [etape,    setEtape]    = useState('motdepasse');
  const [code,     setCode]     = useState('');

  // Session déjà ouverte au mot de passe seul (rechargement de la page pendant le défi) :
  // on reprend directement à l'étape du code au lieu de redemander le mot de passe.
  useEffect(() => {
    let actif = true;
    supabase.auth.getSession().then(({ data, error: errSession }) => {
      if (errSession) { console.error('[SignInModal] Lecture de session :', errSession.message); return; }
      if (actif && defiEnAttente(niveauxDeSession(data?.session))) setEtape('code');
    });
    return () => { actif = false; };
  }, []);

  if (mode === 'modal' && !isOpen) return null;

  const canClose = mode === 'modal' && typeof onClose === 'function';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      /* 1. Authentification */
      const { data, error: signInError } =
        await supabase.auth.signInWithPassword({ email, password });

      if (signInError) {
        setError('Email ou mot de passe incorrect.');
        setLoading(false);
        return;
      }

      /* 2. Double authentification activée (dans le CRM) : la base refuse toute lecture
            tant que le code n'est pas présenté → étape « code ». */
      if (defiEnAttente(niveauxDeSession(data?.session))) {
        setEtape('code');
        setLoading(false);
        return;
      }

      await terminerConnexion(data.user.id);

    } catch (err) {
      console.error('Erreur inattendue lors de la connexion :', err);
      setError(err.message || 'Une erreur réseau est survenue.');
      setLoading(false);
    }
  };

  /* Lecture du rang puis redirection (rang 0 admin / 8 auditeur / autres : même /dashboard). */
  async function terminerConnexion(userId) {
    const { error: profileError } = await supabase
      .from('profiles')
      .select('rang')
      .eq('id', userId)
      .single();
    if (profileError) {
      // Ne pas naviguer : ProtectedRoute renverrait à /login sans message, et l'utilisateur
      // recommencerait mot de passe + code en boucle sans comprendre (cas MFA).
      console.error('Erreur récupération profil :', profileError);
      setError(`Connexion établie, mais votre profil n’a pas pu être lu : ${profileError.message}. `
        + 'Réessayez ; si cela persiste, prévenez l’administrateur.');
      setLoading(false);
      return;
    }
    setLoading(false);
    if (canClose) onClose();
    navigate('/dashboard');
  }

  /* Étape 2 : présentation du code à six chiffres à chaque facteur vérifié. */
  const handleCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!codeBienForme(code)) {
      setError('Saisissez les 6 chiffres affichés par votre application d’authentification.');
      return;
    }
    setLoading(true);
    try {
      const { data: liste, error: errListe } = await supabase.auth.mfa.listFactors();
      if (errListe) throw errListe;
      const facteurs = facteursPourLeDefi(liste);
      const resultat = await presenterLeCodeSurChaqueFacteur({
        mfa: supabase.auth.mfa, facteurs, code: String(code).trim(),
      });
      if (!resultat.succes) {
        resultat.echecs.forEach((er) => console.error('[SignInModal] Défi MFA :', er));
        setError(messageDefiEchoue(resultat.echecs));
        setLoading(false);
        return;
      }
      const { data: s2, error: errSession } = await supabase.auth.getSession();
      if (errSession) throw errSession;
      await terminerConnexion(s2?.session?.user?.id);
    } catch (err) {
      console.error('[SignInModal] Erreur lors de la vérification du code :', err);
      setError(`Votre code n’a pas pu être vérifié : ${err?.message || 'erreur réseau'}. Réessayez.`);
      setLoading(false);
    }
  };

  /* Abandon du défi : on ferme la session ouverte au mot de passe seul. */
  const retourMotDePasse = async () => {
    const { error: errSortie } = await supabase.auth.signOut({ scope: 'local' });
    if (errSortie) console.error('[SignInModal] Déconnexion locale :', errSortie.message);
    setCode('');
    setError('');
    setEtape('motdepasse');
  };

  /* ─── JSX ─── */
  const Card = (
    <div className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all duration-300 ease-in-out">

      {/* Bouton fermer — mode modal uniquement */}
      {mode === 'modal' && (
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
          disabled={loading}
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <h2 className="mb-6 text-center text-2xl font-bold text-gray-800">Connexion</h2>

      {/* Message d'erreur */}
      {error && (
        <div
          className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700"
          role="alert"
        >
          {error}
        </div>
      )}

      {etape === 'code' ? (
      <form onSubmit={handleCode} className="space-y-4">
        <p className="text-sm text-gray-600">
          Double authentification : saisissez le code à 6 chiffres affiché par votre
          application d’authentification (celle configurée dans le CRM).
        </p>
        <div>
          <label htmlFor="signin-code" className="block text-sm font-medium text-gray-700">
            Code de vérification
          </label>
          <input
            id="signin-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            className="mt-1 block w-full rounded-md border-gray-300 p-2 text-center text-lg tracking-widest
                       shadow-sm focus:border-blue-500 focus:ring-blue-500"
            autoFocus
            required
            disabled={loading}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center rounded-md border border-transparent
                     bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm
                     hover:bg-blue-700 focus:outline-none focus:ring-2
                     focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {loading ? 'Vérification…' : 'Valider le code'}
        </button>
        <button
          type="button"
          onClick={retourMotDePasse}
          disabled={loading}
          className="w-full text-sm text-gray-600 hover:text-gray-900 hover:underline disabled:opacity-50"
        >
          Revenir à la connexion
        </button>
      </form>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Email */}
        <div>
          <label htmlFor="signin-email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            id="signin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votreadresse@email.com"
            className="mt-1 block w-full rounded-md border-gray-300 p-2 shadow-sm
                       focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            required
            disabled={loading}
          />
        </div>

        {/* Mot de passe */}
        <div>
          <label htmlFor="signin-password" className="block text-sm font-medium text-gray-700">
            Mot de passe
          </label>
          <div className="relative mt-1">
            <input
              id="signin-password"
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Votre mot de passe"
              className="block w-full rounded-md border-gray-300 p-2 pr-10 shadow-sm
                         focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              required
              disabled={loading}
            />
            {/* Toggle visibilité */}
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3
                         text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label={showPwd ? 'Cacher le mot de passe' : 'Afficher le mot de passe'}
              disabled={loading}
            >
              {showPwd ? (
                /* Icône œil barré */
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg"
                  fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5
                       12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0
                       0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0
                       01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894
                       7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0
                       10-4.243-4.243m4.242 4.242L6.228 6.228" />
                </svg>
              ) : (
                /* Icône œil ouvert */
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg"
                  fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5
                       12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0
                       .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007
                       -9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Se souvenir + mot de passe oublié */}
        <div className="flex flex-wrap items-center justify-between text-sm">
          <label className="flex items-center text-gray-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={loading}
            />
            <span className="ml-2">Se souvenir de moi</span>
          </label>

          <Link
            to="/forgot-password"
            onClick={() => { if (canClose) onClose(); }}
            className={`font-medium text-blue-600 hover:text-blue-500 hover:underline
                        ${loading ? 'pointer-events-none opacity-50' : ''}`}
          >
            Mot de passe oublié ?
          </Link>
        </div>

        {/* Bouton soumettre */}
        <button
          type="submit"
          disabled={loading}
          className="w-full flex justify-center rounded-md border border-transparent
                     bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm
                     hover:bg-blue-700 focus:outline-none focus:ring-2
                     focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {loading ? 'Connexion en cours...' : 'Se connecter'}
        </button>

      </form>
      )}
    </div>
  );

  /* ─── Rendu selon le mode ─── */
  if (mode === 'page') {
    return Card;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center
                    bg-black bg-opacity-50 p-4 transition-opacity duration-300 ease-in-out">
      {Card}
    </div>
  );
};

export default SignInModal;
